import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import {useTranslation} from "react-i18next";
import {useContext, useEffect, useState} from "react";
import ProjectUser, {resolveProjectRolesResponse} from "./ProjectUser.jsx";
import useDownloadState from "../../api/utilities/useDownloadState.js";
import {useSearchParams} from "react-router-dom";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ProjectApi, ProjectPermission} from "../../api/authority/generated/index.ts";
import FormError from "../../components/form/FormError.jsx";
import ProjectInvitationButton from "./ProjectInvitationButton.jsx";
import toast from "react-hot-toast";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import {useHasPermission} from "../../components/auth/rolesAndPermissions.js";
import {Panel, SortableHeader, TablePagination, TableSearch} from "../admin/ReportingLab.jsx";
import ProjectPendingInvitations from "./ProjectPendingInvitations.jsx";

export function nextProjectUsersSort(currentSort, key) {
    return {
        key,
        direction: currentSort.key === key && currentSort.direction === 'asc' ? 'desc' : 'asc',
    };
}

export default function ProjectUsers({project}) {
    let {t} = useTranslation();
    let {et} = useErrorTranslation();

    let hasPermission = useHasPermission();

    let [searchQuery, setSearchQuery] = useState('');
    let [searchValue, setSearchValue] = useState('');
    let [sort, setSort] = useState({key: 'user', direction: 'asc'});
    let [pageSize, setPageSize] = useState(25);
    let [invitationsRefreshKey, setInvitationsRefreshKey] = useState(0);

    let {getAuthorityConfiguration} = useContext(GlobalContext);

    let [state, setDownloading, setResult, setError] = useDownloadState();

    let [searchParams, setSearchParams] = useSearchParams();

    let page = parseInt(searchParams.get('page') ?? 1);


    useEffect(() => {
        let active = true;
        setDownloading(true);
        new ProjectApi(getAuthorityConfiguration())
            .projectUsers({
                page: page,
                pageSize,
                projectId: project.id,
                searchTerm: searchQuery,
                sortBy: sort.key,
                sortDirection: sort.direction,
            })
            .then((r) => {
                if (!active) return;
                setResult(r);
                setDownloading(false);
            })
            .catch((e) => {
                if (!active) return;
                setError(e);
                setDownloading(false);
            });

        return () => { active = false; };
    }, [project.id, page, pageSize, searchQuery, sort.key, sort.direction]);

    useEffect(() => {
        if (searchValue.trim() === searchQuery) {
            return undefined;
        }

        const timeout = window.setTimeout(() => {
            setSearchQuery(searchValue.trim());
            setSearchParams({page: 1});
        }, 300);

        return () => window.clearTimeout(timeout);
    }, [searchQuery, searchValue, setSearchParams]);

    const [projectRoles, setProjectRoles] = useState(null);
    const [projectRolesError, setProjectRolesError] = useState(null);
    const [canRenderUsers, setCanRenderUsers] = useState(false);

    useEffect(() => {
        let active = true;
        setProjectRoles(null);
        setProjectRolesError(null);
        setCanRenderUsers(false);
        new ProjectApi(getAuthorityConfiguration())
            .projectRoles({
                projectId: project.id,
            })
            .then((r) => {
                if (!active) return;

                const rolesResponse = resolveProjectRolesResponse(r);
                setProjectRoles(rolesResponse.roles);
                setProjectRolesError(rolesResponse.error);
                setCanRenderUsers(rolesResponse.canRenderUsers);
            })
            .catch((e) => {
                if (!active) return;

                setProjectRoles([]);
                setProjectRolesError(e);
                setCanRenderUsers(false);
                et(e).then((message) => { if (message) toast.error(message); });
            });

        return () => { active = false; };
    }, [project.id]);

    return (
        <WideSecureLayout title={t('route.project-users')} actions={
            hasPermission(ProjectPermission.ProjectInvitations, project) &&
            <ProjectInvitationButton project={project} projectRoles={projectRoles ?? []} onFinally={() => setInvitationsRefreshKey((current) => current + 1)}/>
        }>
            {hasPermission(ProjectPermission.ProjectInvitations, project) && <ProjectPendingInvitations
                project={project}
                refreshKey={invitationsRefreshKey}
            />}
            <LoadingComponent loading={projectRoles === null}>
                <Panel title={null} loading={state.downloading}>
                    <TableSearch
                        value={searchValue}
                        onChange={setSearchValue}
                        visible
                        t={t}
                    />

                    {(state.error || projectRolesError) && <FormError error={state.error || projectRolesError}/>}

                    {state.result && canRenderUsers && <>
                        <div className="overflow-x-auto">
                            <table>
                                <thead>
                                <tr>
                                    <SortableHeader label={t('project.user')} sortKey="user" sort={sort} onSort={(key) => { setSort((current) => nextProjectUsersSort(current, key)); setSearchParams({page: 1}); }}/>
                                    <SortableHeader label={t('project.role')} sortKey="role" sort={sort} onSort={(key) => { setSort((current) => nextProjectUsersSort(current, key)); setSearchParams({page: 1}); }} align="right"/>
                                    <SortableHeader label={t('project.last-activity-in-project')} sortKey="lastInteraction" sort={sort} onSort={(key) => { setSort((current) => nextProjectUsersSort(current, key)); setSearchParams({page: 1}); }} align="right"/>
                                    <th><span className="sr-only">{t('actions')}</span></th>
                                </tr>
                                </thead>
                                <tbody>
                                    {state.result.data.map((projectUserOutput) => {
                                        return (<ProjectUser
                                            key={projectUserOutput.user.id}
                                            project={project}
                                            projectUser={projectUserOutput}
                                            projectRoles={projectRoles}
                                        />);
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <TablePagination
                            page={state.result.pagination?.page ?? 1}
                            pageSize={state.result.pagination?.itemsPerPage ?? pageSize}
                            totalItems={state.result.pagination?.itemCount ?? state.result.data.length}
                            onPageChange={(nextPage) => setSearchParams({page: nextPage})}
                            onPageSizeChange={(nextPageSize) => { setPageSize(nextPageSize); setSearchParams({page: 1}); }}
                            t={t}
                        />
                    </>}
                </Panel>
            </LoadingComponent>
        </WideSecureLayout>
    );
}
